import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { X } from 'lucide-react';

const Login = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const { login, resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      if (onClose) onClose();
      navigate('/dashboard');
    } catch (err) {
      const code = err?.code || '';
      if (code === 'auth/user-not-found') {
        setError('No account found for this email. Sign up first or check the address.');
      } else if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setError('Incorrect password. Try again or use "Forgot password?" to reset.');
      } else if (code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again later or reset your password.');
      } else if (code === 'auth/user-disabled') {
        setError('This account has been disabled. Contact support.');
      } else {
        setError(err?.message || 'Failed to sign in. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Enter your email to receive a reset link.');
      return;
    }
    setError('');
    setForgotSuccess('');
    setForgotLoading(true);
    try {
      await resetPassword(email.trim());
      setForgotSuccess('Check your email for a link to reset your password.');
      setShowForgot(false);
    } catch (err) {
      const code = err?.code || '';
      if (code === 'auth/user-not-found') {
        setError('No account found for this email.');
      } else {
        setError(err?.message || 'Could not send reset email. Try again.');
      }
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-secondary-900">Sign In</h2>
            {onClose && (
              <button
                onClick={onClose}
                className="text-secondary-400 hover:text-secondary-600"
              >
                <X className="w-6 h-6" />
              </button>
            )}
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                required
              />
              <button
                type="button"
                onClick={() => { setShowForgot(true); setError(''); setForgotSuccess(''); }}
                className="mt-1 text-sm text-primary-600 hover:text-primary-700"
              >
                Forgot password?
              </button>
            </div>

            {forgotSuccess && (
              <div className="p-3 bg-green-100 border border-green-400 text-green-700 rounded text-sm">
                {forgotSuccess}
              </div>
            )}

            {showForgot && (
              <div className="p-3 bg-secondary-50 rounded border border-secondary-200">
                <p className="text-sm text-secondary-700 mb-2">We’ll send a reset link to <strong>{email || 'your email'}</strong>.</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={forgotLoading}
                    className="btn-primary text-sm py-2"
                  >
                    {forgotLoading ? 'Sending…' : 'Send reset link'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForgot(false)}
                    className="text-sm text-secondary-600 hover:text-secondary-800"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </button>

            <p className="text-center text-sm text-secondary-600 mt-4">
              Don&apos;t have an account?{' '}
              <Link
                to="/signup"
                onClick={() => onClose && onClose()}
                className="text-primary-600 hover:text-primary-700 font-medium"
              >
                Sign up
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;